import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { apiConstants, downloadZip } from '../../../common/constants';
import { ABD_INCLUDES, buildBaselineAndFilter, buildBaselineOrFilter } from './constants';

/**
 * V2 export helpers.
 *
 * Two flows:
 *   1) {@link exportXlsx} — FE-only multi-sheet xlsx from rows already in
 *      memory (analytics aggregates, ABD party master, dashboard data).
 *      Zero extra API calls — uses what the dashboard hook returned.
 *   2) {@link exportFilteredCsv} — POST /order/appointment_delivery/export
 *      with the same {@code andfilter / orfilter} body shape as
 *      {@code POST /order/search}. Server returns a CSV stream; we save it
 *      as a blob. Each panel button passes its own {@code extraAndFilter}
 *      (pill / section rule), so the CSV mirrors exactly what the grid
 *      shows for that view. Includes V2 fields (alt_contacts, doc s3 keys,
 *      closure metadata, history count) on top of B2B ABD MIS schema.
 */

/**
 * Build a multi-sheet xlsx and trigger browser download.
 * @param {string} filename without extension
 * @param {Array<{name: string, rows: Array<object>, header?: Array<string>}>} sheets
 */
export function exportXlsx(filename, sheets) {
    if (!Array.isArray(sheets) || sheets.length === 0) return;
    const wb = XLSX.utils.book_new();
    sheets.forEach((s) => {
        const rows = Array.isArray(s.rows) ? s.rows : [];
        if (rows.length === 0) {
            // empty sheet — keep workbook structure consistent
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['No data']]), safeName(s.name));
            return;
        }
        const ws = s.header
            ? XLSX.utils.json_to_sheet(rows, { header: s.header })
            : XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, safeName(s.name));
    });
    const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
    XLSX.writeFile(wb, `${filename}_${stamp}.xlsx`);
}

/**
 * Sync server-side export. Sends the same {@code andfilter / orfilter}
 * body the V2 grid uses, plus any panel-specific {@code extraAndFilter}
 * (pill / section / button-level rule). Server streams CSV (efficient
 * row-by-row write at scale); we parse it FE-side and re-emit as a
 * single-sheet xlsx so the user gets a real Excel file. Server hard-caps
 * rows; if exceeded the FE will save whatever streamed back.
 *
 * @param {object} args
 * @param {object} args.filters {fromDate, toDate, merchant, courier, ...}
 * @param {Array<object>} [args.extraAndFilter] additional FilterCondition[]
 *        merged into the request — equivalent to a clicked pill/section rule
 * @param {Array<object>} [args.extraOrFilter] additional FilterCondition[]
 * @param {string} [args.fileName] suggested download filename (no extension)
 * @param {(msg: string, opts?: object) => void} [args.enqueueSnackbar]
 * @returns {Promise<{ status: 'SUCCESS' | 'ERROR', message?: string }>}
 */
export async function exportFilteredCsv({
    filters = {},
    extraAndFilter = [],
    extraOrFilter = [],
    fileName = 'ABD_Export',
    enqueueSnackbar,
}) {
    try {
        const andfilter = [
            ...buildBaselineAndFilter(filters),
            ...(Array.isArray(extraAndFilter) ? extraAndFilter : []),
        ];
        const orfilter = [
            ...buildBaselineOrFilter(filters),
            ...(Array.isArray(extraOrFilter) ? extraOrFilter : []),
        ];
        const body = {
            andfilter,
            orfilter,
            offset: 0,
            limit: 50000,
            skipCount: true,
            includes: ABD_INCLUDES,
            sort: { field: 'awbRegisteredDate', direction: 'DESC' },
        };
        enqueueSnackbar?.('Generating export…', { variant: 'info' });
        const res = await downloadZip(apiConstants.APPOINTMENT_DELIVERY_EXPORT, body);
        if (!res || !res.data) {
            const msg = 'Export failed — empty response';
            enqueueSnackbar?.(msg, { variant: 'error' });
            return { status: 'ERROR', message: msg };
        }
        // axios responseType=blob/arraybuffer → coerce to text, then let SheetJS
        // parse the CSV (it handles quoted fields + embedded newlines correctly)
        // and write a single-sheet xlsx. Memory peaks at ~2x CSV size during
        // conversion; acceptable for the server's 50k-row cap.
        const csvText = typeof res.data === 'string'
            ? res.data
            : (res.data instanceof Blob ? await res.data.text()
                : new TextDecoder('utf-8').decode(res.data));
        const wb = XLSX.read(csvText, { type: 'string' });
        const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
        XLSX.writeFile(wb, `${fileName}_${stamp}.xlsx`);
        enqueueSnackbar?.('Export ready', { variant: 'success' });
        return { status: 'SUCCESS' };
    } catch (e) {
        // axios responseType=blob → error body comes back as Blob, not parsed
        // JSON. Read it as text so the real BE message reaches the user.
        let msg = e?.message || 'Export failed';
        const errBlob = e?.response?.data;
        if (errBlob && typeof errBlob.text === 'function') {
            try {
                const text = await errBlob.text();
                try {
                    const parsed = JSON.parse(text);
                    msg = parsed?.message || parsed?.exception || text || msg;
                } catch {
                    msg = text || msg;
                }
            } catch {
                // Use the fallback msg.
            }
        } else if (typeof errBlob === 'string') {
            msg = errBlob;
        } else if (errBlob?.message) {
            msg = errBlob.message;
        }
        enqueueSnackbar?.(msg, { variant: 'error' });
        return { status: 'ERROR', message: msg };
    }
}

// Excel sheet names cap at 31 chars + can't include /\?*[]:
function safeName(name) {
    return String(name || 'Sheet').replace(/[\\/?*[\]:]/g, '_').slice(0, 31);
}
